import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Mail, RefreshCw, Link2, Unlink, CheckCircle2, AlertCircle, Clock, Search, Zap, Plus, Trash2, Building2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

const formatDate = (s) =>
  s ? new Date(s).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "-";

export default function AdminGmailBot() {
  const [status, setStatus] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [pendingTopups, setPendingTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [matchSel, setMatchSel] = useState({}); // transferId -> requestId
  const [senders, setSenders] = useState([]);
  const [newSender, setNewSender] = useState("");

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/gmail/status`, { headers: authHeader() });
      setStatus(res.data);
    } catch (err) {
      toast.error("Gagal memuat status Gmail");
    }
  }, []);

  const fetchTransfers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/gmail/incoming-transfers`, { headers: authHeader() });
      setTransfers(res.data.transfers || []);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/customer-topup-requests?status=pending`, { headers: authHeader() });
      setPendingTopups(res.data.requests || []);
    } catch (err) { /* ignore */ }
  }, []);

  const fetchSenders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/gmail/senders`, { headers: authHeader() });
      setSenders(res.data.senders || []);
    } catch (err) { /* ignore */ }
  }, []);

  const saveSenders = async (list) => {
    try {
      const res = await axios.post(`${API}/admin/gmail/senders`, { senders: list }, { headers: authHeader() });
      setSenders(res.data.senders || []);
      toast.success("Sumber email disimpan");
    } catch (err) {
      toast.error("Gagal menyimpan sumber email");
    }
  };

  const addSender = () => {
    const s = newSender.trim();
    if (!s) return;
    if (senders.includes(s)) { toast.error("Sumber sudah ada"); return; }
    saveSenders([...senders, s]);
    setNewSender("");
  };

  const removeSender = (s) => saveSenders(senders.filter((x) => x !== s));

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchTransfers(), fetchPending(), fetchSenders()]);
      setLoading(false);
    })();
    // Handle OAuth redirect result
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      toast.success("Gmail berhasil terhubung!");
      window.history.replaceState({}, "", "/admin/gmail");
    } else if (params.get("error")) {
      toast.error("Koneksi Gmail gagal: " + params.get("error"));
      window.history.replaceState({}, "", "/admin/gmail");
    }
  }, [fetchStatus, fetchTransfers, fetchPending, fetchSenders]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/admin/gmail/auth-url`, { headers: authHeader() });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memulai koneksi Gmail");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/admin/gmail/disconnect`, {}, { headers: authHeader() });
      toast.success("Koneksi Gmail diputuskan");
      fetchStatus();
    } catch (err) {
      toast.error("Gagal memutus koneksi");
    }
  };

  const handlePoll = async () => {
    setPolling(true);
    try {
      const res = await axios.post(`${API}/admin/gmail/poll`, {}, { headers: authHeader() });
      const d = res.data;
      if (!d.connected) {
        toast.error("Gmail belum terhubung");
      } else if (d.error) {
        toast.error("Gagal memindai: " + d.error);
      } else {
        toast.success(`Pindai selesai: ${d.processed} transfer baru, ${d.matched} cocok otomatis`);
      }
      await Promise.all([fetchStatus(), fetchTransfers(), fetchPending()]);
    } catch (err) {
      toast.error("Gagal memindai Gmail");
    } finally {
      setPolling(false);
    }
  };

  const handleManualMatch = async (transferId) => {
    const requestId = matchSel[transferId];
    if (!requestId) {
      toast.error("Pilih permintaan top-up dulu");
      return;
    }
    try {
      await axios.post(`${API}/admin/gmail/incoming-transfers/${transferId}/match`, { request_id: requestId }, { headers: authHeader() });
      toast.success("Berhasil dicocokkan & saldo ditambahkan");
      await Promise.all([fetchTransfers(), fetchPending()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mencocokkan");
    }
  };

  const connected = status?.connected;

  return (
    <AdminLayout title="Bot Pembayaran (Gmail)">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        {/* Connection card */}
        <Card data-testid="gmail-connection-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="w-5 h-5 text-sky-600" /> Koneksi Gmail
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!status?.configured && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                Kredensial Google belum dikonfigurasi di server.
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${connected ? "bg-emerald-100" : "bg-slate-100"}`}>
                  {connected ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <Unlink className="w-6 h-6 text-slate-400" />}
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-white" data-testid="gmail-status-text">
                    {connected ? "Terhubung" : "Belum terhubung"}
                  </p>
                  <p className="text-sm text-slate-500">
                    {connected ? (status.email || "Akun Gmail") : "Hubungkan Gmail Anda untuk deteksi transfer otomatis"}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {connected ? (
                  <Button variant="outline" onClick={handleDisconnect} className="gap-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="gmail-disconnect-btn">
                    <Unlink className="w-4 h-4" /> Putuskan
                  </Button>
                ) : (
                  <Button onClick={handleConnect} disabled={connecting || !status?.configured} className="gap-2 bg-sky-600 hover:bg-sky-700" data-testid="gmail-connect-btn">
                    <Link2 className="w-4 h-4" /> {connecting ? "Mengalihkan..." : "Hubungkan Gmail"}
                  </Button>
                )}
              </div>
            </div>
            {connected && (
              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-slate-100 dark:border-slate-800 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Pindai terakhir: {formatDate(status.last_poll)}</span>
                <span>Sumber dipantau: <b className="text-slate-700 dark:text-slate-300">{(status.senders || senders).length} alamat</b></span>
                <Button size="sm" variant="outline" onClick={handlePoll} disabled={polling} className="gap-2 ml-auto" data-testid="gmail-poll-btn">
                  <Search className={`w-4 h-4 ${polling ? "animate-pulse" : ""}`} /> {polling ? "Memindai..." : "Pindai Sekarang"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sumber Email Bank / E-Wallet */}
        <Card data-testid="gmail-senders-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="w-5 h-5 text-sky-600" /> Sumber Email Bank / E-Wallet
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-500">
              Alamat email pengirim notifikasi yang dipindai bot. Tambahkan bank atau e-wallet lain di sini (mis. notifikasi BCA, DANA, OVO).
            </p>
            <div className="flex gap-2">
              <Input
                value={newSender}
                onChange={(e) => setNewSender(e.target.value)}
                placeholder="mis. notifikasi@bca.co.id"
                data-testid="sender-input"
                onKeyDown={(e) => { if (e.key === "Enter") addSender(); }}
              />
              <Button onClick={addSender} className="gap-1.5 bg-sky-600 hover:bg-sky-700 shrink-0" data-testid="add-sender-btn">
                <Plus className="w-4 h-4" /> Tambah
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {senders.length === 0 ? (
                <span className="text-sm text-slate-400">Belum ada sumber email.</span>
              ) : senders.map((s) => (
                <span key={s} className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-full pl-3 pr-1.5 py-1 text-sm" data-testid={`sender-chip-${s}`}>
                  {s}
                  <button onClick={() => removeSender(s)} className="w-5 h-5 rounded-full hover:bg-red-100 flex items-center justify-center text-red-500" title="Hapus">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* How it works */}
        <div className="bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900 rounded-xl p-4 text-sm text-slate-600 dark:text-slate-300 flex items-start gap-3">
          <Zap className="w-5 h-5 text-sky-600 mt-0.5 shrink-0" />
          <div>
            Bot memindai email notifikasi transfer masuk dari bank setiap 2 menit. Jika nominal transfer <b>sama persis</b> dengan nominal unik permintaan top-up pelanggan yang menunggu, saldo otomatis ditambahkan.
            Transfer yang <b>tidak cocok</b> tampil di bawah agar Anda bisa cocokkan manual.
          </div>
        </div>

        {/* Incoming transfers */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Transfer Masuk Terdeteksi</CardTitle>
            <Button size="sm" variant="ghost" onClick={fetchTransfers} className="gap-1.5"><RefreshCw className="w-4 h-4" /> Muat ulang</Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
            ) : transfers.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                Belum ada transfer masuk terdeteksi.
              </div>
            ) : (
              <div className="space-y-3">
                {transfers.map((t) => (
                  <div key={t.id} className="border border-slate-200 dark:border-slate-800 rounded-xl p-4" data-testid={`gmail-transfer-${t.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-xl font-bold text-slate-800 dark:text-white">
                          {t.amount != null ? formatCurrency(t.amount) : "Nominal tidak terbaca"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatDate(t.received_at)}{t.acct_last4 ? ` • rek ****${t.acct_last4}` : ""}{t.ref ? ` • Ket: ${t.ref.slice(0, 16)}...` : ""}
                        </p>
                        {t.sender_name && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Atas nama: <b>{t.sender_name}</b></p>}
                      </div>
                      {t.status === "matched" ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-4 h-4" /> Cocok: {t.matched_customer_name || "pelanggan"}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                          <AlertCircle className="w-4 h-4" /> Belum cocok
                        </span>
                      )}
                    </div>

                    {t.status !== "matched" && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Select value={matchSel[t.id] || ""} onValueChange={(v) => setMatchSel((s) => ({ ...s, [t.id]: v }))}>
                          <SelectTrigger className="w-full sm:w-80" data-testid={`match-select-${t.id}`}>
                            <SelectValue placeholder="Pilih permintaan top-up untuk dicocokkan" />
                          </SelectTrigger>
                          <SelectContent>
                            {pendingTopups.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-slate-400">Tidak ada permintaan menunggu</div>
                            ) : pendingTopups.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.customer_name} — {formatCurrency(p.amount)}{p.transfer_amount ? ` (transfer ${formatCurrency(p.transfer_amount)})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={() => handleManualMatch(t.id)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`match-btn-${t.id}`}>
                          <CheckCircle2 className="w-4 h-4" /> Cocokkan & Tambah Saldo
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </AdminLayout>
  );
}
